/**
 * @file パスワードリセット（本人確認）ページ
 *
 * 姓名・生年月日・連絡先電話番号で本人確認を行い、
 * SMSでワンタイムパスワードを送信する。
 * 同一情報の契約が複数ある場合は「お客さま番号」入力欄を追加表示する。
 */
import { yupResolver } from '@hookform/resolvers/yup';
import type { NextPage } from 'next';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { ChangeEvent, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import api from '@/utils/api';
import { isExpiredToken } from '@/utils/cognito';
import url from '@/utils/config';
import { ApiUrl } from '@/utils/constants';
import { existsBirthDate, getBirthDateDayList, getBirthDateMonthList, getBirthDateYearList, zeroPadding } from '@/utils/date';
import yup from '@/utils/yup.string';

import historyContext from '@/hooks/historyContext';
import useSetLoading from '@/hooks/useSetLoading';
import { alertState } from '@/states/alertState';
import { PasswordResetForm, passwordResetFormState } from '@/states/passwordResetFormState';
import { providerInfoState } from '@/states/providerInfoState';
import { OnetimePasswordRequest } from '@/types/api/mypage/onetimePassword';

import LinkButton from '@/components/atoms/LinkButton';
import SystemErrorMessage from '@/components/atoms/SystemErrorMessage';
import AtErrorMessage from '@/components/atoms/form/AtErrorMessage';
import AtInput from '@/components/atoms/form/AtInput';
import AtPulldownList from '@/components/atoms/form/AtPulldownList';
import MlLabeledTextBox from '@/components/atoms/molecules/MlLabeledTextBox';

const PasswordReset: NextPage = () => {
  const router = useRouter();
  const setAlert = useSetRecoilState(alertState);
  const providerInfo = useRecoilValue(providerInfoState);
  const [passwordResetForm, setPasswordResetForm] = useRecoilState(passwordResetFormState);
  const [birthDateDayPullDown, setBirthDateDayPullDown] = useState<{ key: number; value: string; label: string }[]>(
    getBirthDateDayList(passwordResetForm.birthDateYear, passwordResetForm.birthDateMonth)
  );
  const history = useContext(historyContext);
  const [localErrorMessage, setLocalErrorMessage] = useState('');
  // 同一の姓名・生年月日で複数契約が見つかった場合にtrue → お客さま番号（ISP契約番号）入力欄を追加表示する
  const [isMoreThanOneCustomerFound, setIsMoreThanOneCustomerFound] = useState(false);
  const { startLoading, finishLoading } = useSetLoading();

  const schema = yup.object().shape({
    lsName: yup.string().required('姓、名を入力してください。').max(32, '姓、名は32文字以内で入力してください。').zenkaku('姓、名は全角で入力してください。'),
    flName: yup.string().required('姓、名を入力してください。').max(32, '姓、名は32文字以内で入力してください。').zenkaku('姓、名は全角で入力してください。'),
    phoneNumber: yup
      .string()
      .required('連絡先電話番号を入力してください。')
      .min(11, '連絡先電話番号は半角数字11桁で入力してください。')
      .max(11, '連絡先電話番号は半角数字11桁で入力してください。')
      .telno('連絡先電話番号は半角数字11桁で入力してください。'),
    birthDateYear: yup.string().required('生年月日を全て入力してください。'),
    birthDateMonth: yup.string().required('生年月日を全て入力してください。'),
    birthDateDay: yup.string().required('生年月日を全て入力してください。'),
    ispNumber: yup
      .string()
      .test('required', 'お客さま番号を入力してください。', (value) => {
        if (!isMoreThanOneCustomerFound || value !== '') {
          return true;
        }
        return false;
      })
      .matches(/^([a-zA-Z0-9]{16})$/, 'お客さま番号の文字列が不正です。入力内容をご確認ください。'),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PasswordResetForm>({
    resolver: yupResolver(schema) as any,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  // 遷移元に応じてフォームの初期化を分岐する
  // ログイン画面 or 直接アクセス → フォームを空にリセット
  // ワンタイムパスワード画面から戻った場合 → 入力済みの値を保持して復元
  useEffect(() => {
    // 初期表示時
    if (history[0].includes('login') || history[0] === '/password-reset/') {
      setPasswordResetForm({
        ...passwordResetForm,
        lsName: '',
        flName: '',
        birthDateYear: '',
        birthDateMonth: '',
        birthDateDay: '',
        phoneNumber: '',
        ispNumber: '',
      });
      setIsMoreThanOneCustomerFound(false);
    } else {
      setPasswordResetForm({
        ...passwordResetForm,
        lsName: passwordResetForm.lsName,
        flName: passwordResetForm.flName,
        birthDateYear: passwordResetForm.birthDateYear,
        birthDateMonth: passwordResetForm.birthDateMonth,
        birthDateDay: passwordResetForm.birthDateDay,
        phoneNumber: passwordResetForm.phoneNumber,
        ispNumber: passwordResetForm.ispNumber,
      });
      // お客さま番号が入力済みなら複数契約モードを復元する
      setIsMoreThanOneCustomerFound(passwordResetForm.ispNumber !== '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updatePasswordResetFormInput = (e: ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setPasswordResetForm({ ...passwordResetForm, [id]: value });
  };

  const updatePasswordResetFormSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const { id, value } = e.target;
    setPasswordResetForm({ ...passwordResetForm, [id]: value });
  };

  // 年変更時: 日プルダウンを再構築する（うるう年の2/29対応のため）
  const updateBirthDateYear = (e: ChangeEvent<HTMLSelectElement>) => {
    updatePasswordResetFormSelect(e);
    resetBirthDateDay(e.target.value, passwordResetForm.birthDateMonth, passwordResetForm.birthDateDay);
  };

  // 月変更時: 日プルダウンを再構築する（月ごとの日数が異なるため）
  const updateBirthDateMonth = (e: ChangeEvent<HTMLSelectElement>) => {
    updatePasswordResetFormSelect(e);
    resetBirthDateDay(passwordResetForm.birthDateYear, e.target.value, passwordResetForm.birthDateDay);
  };

  // 年・月の変更に連動して日の選択肢を更新し、存在しない日付（例: 2/30）が選択済みなら日をクリアする
  const resetBirthDateDay = (_birthDateYear?: string, _birthDateMonth?: string, _birthDateDay?: string) => {
    setBirthDateDayPullDown(getBirthDateDayList(_birthDateYear, _birthDateMonth));
    if (_birthDateYear && _birthDateMonth && _birthDateDay && !existsBirthDate(_birthDateYear, _birthDateMonth, _birthDateDay)) {
      setPasswordResetForm({
        ...passwordResetForm,
        birthDateYear: _birthDateYear,
        birthDateMonth: _birthDateMonth,
        birthDateDay: '',
      });
    }
  };

  // API送信用に「YYYY-MM-DD」形式の日付文字列を構築する（月・日は0埋め2桁）
  const getBirthDateParam = (): string => {
    return `${passwordResetForm.birthDateYear}-${zeroPadding(Number(passwordResetForm.birthDateMonth), 2)}-${zeroPadding(Number(passwordResetForm.birthDateDay), 2)}`;
  };

  // SMS認証APIに送信するパラメータを構築する。複数契約時のみisp_cn_noを付与する
  const getOnetimePasswordRequestParam = (): OnetimePasswordRequest => {
    const param: OnetimePasswordRequest = {
      ls_name: passwordResetForm.lsName,
      fl_name: passwordResetForm.flName,
      birthday: getBirthDateParam(),
      tel: passwordResetForm.phoneNumber,
      isp_cn_no: isMoreThanOneCustomerFound ? passwordResetForm.ispNumber : undefined,
      provider_id: providerInfo.providerId,
    };

    return param;
  };

  // SMS認証
  const smsAuth = async (): Promise<void> => {
    startLoading(ApiUrl.NonProtected.SMS_AUTH);
    setPasswordResetForm(passwordResetForm);

    await api
      .post(ApiUrl.NonProtected.SMS_AUTH, getOnetimePasswordRequestParam(), {
        headers: { 'content-type': 'application/json' },
      })
      .then(() => {
        router.push('/password-reset/onetime-password');
      })
      .catch((error) => {
        if (isExpiredToken(error)) {
          sessionStorage.clear();
          window.location.href = url('/login');
          return;
        }
        const errorCode = error.response.data?.error_code;

        // SMS送信の連続試行でレート制限に達した場合
        if (errorCode === 'TOO_MANY_ATTEMPTS') {
          setLocalErrorMessage('ワンタイムパスワードの送信を連続して行ったため、ワンタイムパスワードの送信をロックしました。1日以上時間をおいてから再度お試しください。');
        } else if (errorCode === 'MORE_THAN_ONE_CUSTOMER_FOUND') {
          // 同一本人情報で複数契約がヒットした場合 → お客さま番号の追加入力を求める
          setIsMoreThanOneCustomerFound(true);
        } else {
          setAlert({
            isOpen: true,
            apiType: '',
          });
        }
      })
      .finally(() => finishLoading(ApiUrl.NonProtected.SMS_AUTH));
  };

  // 送信行う
  const isValid = (): Promise<void> => {
    return smsAuth();
  };

  // バリエーションチェック通らず
  const isInValid = () => {
    setLocalErrorMessage('入力内容が正しくありません。');
  };

  return (
    <main>
      <div className='form-area'>
        <p className='left mb15'>
          ご契約者様の氏名、連絡先電話番号、生年月日を入力し、「ワンタイムパスワード送信」ボタンをクリックしてください。ご登録の電話番号宛にワンタイムパスワードをSMSで送信します。
        </p>
        {localErrorMessage && <SystemErrorMessage message={localErrorMessage} />}

        <dl className='row1'>
          <dt className='taLeft'>氏名</dt>
          <dd className='name'>
            <label htmlFor='lsName'>姓</label>{' '}
            <AtInput
              type='text'
              id='lsName'
              value={passwordResetForm.lsName}
              register={register('lsName', {
                onChange: (e) => {
                  updatePasswordResetFormInput(e);
                },
              })}
              error={errors.lsName}
            />{' '}
            <label htmlFor='flName'>名</label>{' '}
            <AtInput
              type='text'
              id='flName'
              value={passwordResetForm.flName}
              register={register('flName', {
                onChange: (e) => {
                  updatePasswordResetFormInput(e);
                },
              })}
              error={errors.flName}
            />
            <AtErrorMessage error={errors.lsName} />
            {!errors.lsName && <AtErrorMessage error={errors.flName} />}
          </dd>

          <MlLabeledTextBox
            type='text'
            id='phoneNumber'
            dtClassName='taLeft'
            value={passwordResetForm.phoneNumber}
            register={register('phoneNumber', {
              onChange: (e) => {
                updatePasswordResetFormInput(e);
              },
            })}
            error={errors.phoneNumber}
          >
            連絡先電話番号
          </MlLabeledTextBox>

          <dt className='taLeft'>生年月日</dt>
          <dd className='birthday'>
            <AtPulldownList
              id='birthDateYear'
              options={getBirthDateYearList()}
              value={passwordResetForm.birthDateYear}
              register={register('birthDateYear')}
              onChange={updateBirthDateYear}
              error={errors.birthDateYear}
              blank
            />{' '}
            <label htmlFor='birthDateYear'>年</label>{' '}
            <AtPulldownList
              id='birthDateMonth'
              options={getBirthDateMonthList()}
              value={passwordResetForm.birthDateMonth}
              register={register('birthDateMonth')}
              onChange={updateBirthDateMonth}
              error={errors.birthDateMonth}
              blank
            />{' '}
            <label htmlFor='birthDateMonth'>月</label>{' '}
            <AtPulldownList
              id='birthDateDay'
              options={birthDateDayPullDown}
              value={passwordResetForm.birthDateDay}
              register={register('birthDateDay')}
              onChange={updatePasswordResetFormSelect}
              error={errors.birthDateDay}
              blank
            />{' '}
            <label htmlFor='birthDateDay'>日</label>
            <AtErrorMessage error={errors.birthDateYear} />
            {!errors.birthDateYear && <AtErrorMessage error={errors.birthDateMonth} />}
            {!errors.birthDateYear && !errors.birthDateMonth && <AtErrorMessage error={errors.birthDateDay} />}
          </dd>

          {isMoreThanOneCustomerFound && (
            <>
              <MlLabeledTextBox
                type='text'
                id='ispNumber'
                dtClassName='taLeft'
                value={passwordResetForm.ispNumber}
                register={register('ispNumber', {
                  onChange: (e) => {
                    updatePasswordResetFormInput(e);
                  },
                })}
                error={errors.ispNumber}
              >
                お客さま番号
              </MlLabeledTextBox>

              <p className='taLeft'>
                お客様は複数のご契約をお持ちのため、ご希望の契約のお客さま番号をご入力ください。
                <br />
                お客さま番号は契約内容通知書に記載しています。
              </p>
            </>
          )}
        </dl>

        <p className='link'>
          <Link href='/password-reset/onetime-password'>ワンタイムパスワード送信済みの方はこちら</Link>
        </p>
        <LinkButton onClick={handleSubmit(isValid, isInValid)}>ワンタイムパスワード送信</LinkButton>
      </div>
    </main>
  );
};

export default PasswordReset;
